/**
 * @fileoverview Unified drawer configuration for responsive navigation across
 * mobile, tablet, and desktop viewports.
 */

import * as React from 'react';
import Drawer from '@mui/material/Drawer';

export interface DrawerConfig {
  variant: 'temporary' | 'permanent';
  display: { xs: string; sm: string; md: string };
  isCollapsed: boolean;
  viewport: 'phone' | 'tablet' | 'desktop';
  open?: boolean;
  onClose?: () => void;
}

export interface UnifiedDrawerProps {
  config: DrawerConfig;
  children: React.ReactNode;
  drawerSx: React.CSSProperties | object;
  appWindowDocument?: Document;
}

export function UnifiedDrawer(props: UnifiedDrawerProps): React.JSX.Element {
  const { config, children, drawerSx, appWindowDocument } = props;

  if (config.variant === 'temporary') {
    return (
      <Drawer
        anchor="right"
        container={appWindowDocument?.body}
        variant="temporary"
        open={config.open}
        onClose={config.onClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={drawerSx}
      >
        {children}
      </Drawer>
    );
  }

  return (
    <Drawer variant="permanent" sx={drawerSx}>
      {children}
    </Drawer>
  );
}

/* eslint-disable react-refresh/only-export-components */
export function createDrawerConfigs(
  disableCollapsibleSidebar: boolean,
  isMobileNavigationExpanded: boolean,
  isMobileCollapsed: boolean,
  isDesktopCollapsed: boolean,
): DrawerConfig[] {
  return [
    {
      variant: 'temporary',
      display: {
        xs: 'block',
        sm: disableCollapsibleSidebar ? 'block' : 'none',
        md: 'none',
      },
      isCollapsed: false,
      viewport: 'phone',
      open: isMobileNavigationExpanded,
    },
    {
      variant: 'permanent',
      display: {
        xs: 'none',
        sm: disableCollapsibleSidebar ? 'none' : 'block',
        md: 'none',
      },
      isCollapsed: isMobileCollapsed,
      viewport: 'tablet',
    },
    {
      variant: 'permanent',
      display: { xs: 'none', md: 'block', sm: 'none' },
      isCollapsed: isDesktopCollapsed,
      viewport: 'desktop',
    },
  ];
}
